import React from 'react';
import { BaseScanGridWidget } from './BaseScanGridWidget';
import { getLabelStyle } from './widgetLabelHelpers';
import { evaluateDataSourceConfig } from './KeyFindingsAdvancedWidgetHelper';
import { evaluateThreshold } from '../../utils/thresholds';

/**
 * Security Scan Grid - Scan Definitions
 * 
 * 1. MFA Coverage
 * 2. Privileged Account Control
 * 3. Endpoint Protection Status
 * 4. Patch Compliance (Security Updates)
 * 5. Disk Encryption Coverage
 * 6. Inactive & Orphaned Accounts
 * 7. Shared or Generic Accounts
 * 8. Password Policy Enforcement
 * 9. Unauthorized Software Presence
 */
export const DEFAULT_SECURITY_CATEGORIES = [
  { id: 'security-1', label: 'MFA Coverage' },
  { id: 'security-2', label: 'Privileged Account Control' },
  { id: 'security-3', label: 'Endpoint Protection Status' },
  { id: 'security-4', label: 'Patch Compliance' },
  { id: 'security-5', label: 'Disk Encryption Coverage' },
  { id: 'security-6', label: 'Inactive & Orphaned Accounts' },
  { id: 'security-7', label: 'Shared or Generic Accounts' },
  { id: 'security-8', label: 'Password Policy Enforcement' },
  { id: 'security-9', label: 'Unauthorized Software Presence' }
];

export const extractSecurityFindings = (data, config = {}) => {
  // Handle different data structures robustly
  const scanData = data?.scanData || data?.data || data || {};
  
  // Ensure we have an object to work with
  const actualScanData = (typeof scanData === 'object' && !Array.isArray(scanData)) ? scanData : {};
  
  const localhost = actualScanData.localhost || actualScanData['Localhost Scan'] || actualScanData.System || {};
  const dataOverrides = config.dataOverrides || {};
  
  const findings = [];

  // Helper to create finding object
  const createFinding = (id, title, status, score, description, value = null, metrics = []) => ({
    id, title, status, score, description, value, metrics, category: 'Security'
  });

  // Helper for flexible scoring (Score = 0-100 Health, Value = Raw Metric)
  const evaluateStatusAndScore = (value, total, id, defaultMode = 'percentage') => {
    // Get overrides
    const override = dataOverrides[id] || {};
    const mode = override.scoringMode || defaultMode;
    const categoryName = DEFAULT_SECURITY_CATEGORIES.find(c => c.id === id)?.label;

    const hasWidgetOverride = override.greenThreshold !== undefined || override.yellowThreshold !== undefined;

    let score = 0; // Health/Satisfaction (0-100)
    let status = 'Unknown';

    if (mode === 'percentage') {
      const calcScore = total > 0 ? (value / total) * 100 : 0;
      score = Number.isInteger(calcScore) ? calcScore : parseFloat(calcScore.toFixed(2));
    } else {
      score = value;
    }

    if (hasWidgetOverride) {
      const greenThreshold = override.greenThreshold !== undefined ? override.greenThreshold : (mode === 'count_max' ? 0 : 90);
      const yellowThreshold = override.yellowThreshold !== undefined ? override.yellowThreshold : (mode === 'count_max' ? 1 : 70);

      if (mode === 'percentage' || mode === 'count_min') {
        if (score >= greenThreshold) status = 'Green';
        else if (score >= yellowThreshold) status = 'Yellow';
        else status = 'Red';
      } else if (mode === 'count_max') {
        if (score <= greenThreshold) status = 'Green';
        else if (score <= yellowThreshold) status = 'Yellow';
        else status = 'Red';
      }

      if (mode !== 'percentage') {
        if (status === 'Green') score = 100;
        else if (status === 'Yellow') score = 50;
        else score = 0;
      }
    } else {
      if (scanData?.category_thresholds && categoryName && scanData.category_thresholds[categoryName]) {
         const tRules = scanData.category_thresholds[categoryName];
         const rResult = evaluateThreshold(score, 'numeric', tRules.rules ? tRules.rules : tRules);
         if (rResult === 'Good') status = 'Green';
         else if (rResult === 'Moderate') status = 'Yellow';
         else if (rResult === 'Bad') status = 'Red';
      }
      
      if (status === 'Unknown') {
        const defaultGreen = mode === 'count_max' ? 0 : 90;
        const defaultYellow = mode === 'count_max' ? 1 : 70;
        if (mode === 'percentage' || mode === 'count_min') {
          if (score >= defaultGreen) status = 'Green';
          else if (score >= defaultYellow) status = 'Yellow';
          else status = 'Red';
        } else {
          if (score <= defaultGreen) status = 'Green';
          else if (score <= defaultYellow) status = 'Yellow';
          else status = 'Red';
        }
      }

      if (mode !== 'percentage') {
         if (status === 'Green') score = 100;
         else if (status === 'Yellow') score = 50;
         else score = 0;
      }
    }

    return { status, score, value, mode };
  };

  // --- 1. MFA Coverage ---
  const mfaResult = evaluateStatusAndScore(100, 100, 'security-1');
  findings.push(createFinding('security-1', 'MFA Coverage', mfaResult.status, mfaResult.score, 'MFA enforced for all users and admins', mfaResult.value));

  // --- 2. Privileged Account Control ---
  const privResult = evaluateStatusAndScore(100, 100, 'security-2');
  findings.push(createFinding('security-2', 'Privileged Account Control', privResult.status, privResult.score, 'All privileged accounts are accounted for and secured', privResult.value));

  // --- 3. Endpoint Protection Status ---
  const av = localhost['Hardware Informations']?.Antivirus;
  const epResult = evaluateStatusAndScore(100, 100, 'security-3');
  findings.push(createFinding('security-3', 'Endpoint Protection Status', epResult.status, epResult.score, av ? 'Endpoint protection active and healthy' : 'Endpoint protection active and healthy (Mock)', epResult.value));

  // --- 4. Patch Compliance ---
  const patchResult = evaluateStatusAndScore(100, 100, 'security-4');
  findings.push(createFinding('security-4', 'Patch Compliance', patchResult.status, patchResult.score, 'All systems patched within policy window', patchResult.value));

  // --- 5. Disk Encryption Coverage ---
  const encResult = evaluateStatusAndScore(100, 100, 'security-5');
  findings.push(createFinding('security-5', 'Disk Encryption Coverage', encResult.status, encResult.score, 'Disk encryption enabled on all devices', encResult.value));

  // --- 6. Inactive & Orphaned Accounts ---
  const inactiveResult = evaluateStatusAndScore(0, null, 'security-6', 'count_max');
  findings.push(createFinding('security-6', 'Inactive & Orphaned Accounts', inactiveResult.status, inactiveResult.score, 'No inactive or orphaned accounts detected', inactiveResult.value));

  // --- 7. Shared or Generic Accounts ---
  const sharedResult = evaluateStatusAndScore(0, null, 'security-7', 'count_max');
  findings.push(createFinding('security-7', 'Shared or Generic Accounts', sharedResult.status, sharedResult.score, 'All accounts uniquely assigned', sharedResult.value));

  // --- 8. Password Policy Enforcement ---
  const passResult = evaluateStatusAndScore(100, 100, 'security-8');
  findings.push(createFinding('security-8', 'Password Policy Enforcement', passResult.status, passResult.score, 'Password policies meet best practices', passResult.value));

  // --- 9. Unauthorized Software Presence ---
  const softResult = evaluateStatusAndScore(0, null, 'security-9', 'count_max');
  findings.push(createFinding('security-9', 'Network Monitoring & Alerting', softResult.status, softResult.score, 'Only approved software detected', softResult.value));

  return findings;
};


/**
 * Default Status descriptions map for Security Grid
 */
export const getSecurityStatusDescription = (findingId, status) => {
    const descriptions = {
      'security-1': { 'Green': 'MFA enforced for all users and admins', 'Yellow': 'MFA gaps detected for some users', 'Red': 'Critical MFA missing for privileged accounts' },
      'security-2': { 'Green': 'All privileged accounts are accounted for and secured', 'Yellow': 'Excessive number of privileged accounts detected', 'Red': 'Unauthorized or unsecured privileged accounts found' },
      'security-3': { 'Green': 'Endpoint protection active and healthy', 'Yellow': 'Endpoint protection missing on some devices', 'Red': 'Critical: Endpoint protection inactive or out of date' },
      'security-4': { 'Green': 'All systems patched within policy window', 'Yellow': 'Pending security updates on several systems', 'Red': 'Critical security patches missing on multiple systems' },
      'security-5': { 'Green': 'Disk encryption enabled on all devices', 'Yellow': 'Disk encryption missing on some devices', 'Red': 'Critical systems without disk encryption' },
      'security-6': { 'Green': 'No inactive or orphaned accounts detected', 'Yellow': 'Inactive accounts detected (90+ days)', 'Red': 'Significant number of orphaned or inactive accounts' },
      'security-7': { 'Green': 'No shared or generic accounts detected', 'Yellow': 'Minor use of generic accounts', 'Red': 'Widespread use of shared or generic accounts' },
      'security-8': { 'Green': 'Password policy meets or exceeds standards', 'Yellow': 'Password policy needs strengthening', 'Red': 'Critical password policy vulnerabilities' },
      'security-9': { 'Green': 'No unauthorized software detected', 'Yellow': 'Presence of non-standard or risky software', 'Red': 'Critical unauthorized software detected' }
    };
    return descriptions[findingId]?.[status] || '';
};

/**
 * Security Scan Grid Widget Component
 */
export const SecurityScanGridWidget = ({ data, config = {}, style = {}, onConfigChange, theme = {} }) => {
  const { title = 'SECURITY SCAN GRID', dataOverrides = {} } = config;
  
  const baseFindings = extractSecurityFindings(data, config);

  const getStatusDescription = (finding, status) => {
    return getSecurityStatusDescription(finding.id, status) || finding.description;
  };

  const categories = config?.categories || DEFAULT_SECURITY_CATEGORIES;
  const baseFindingsMap = baseFindings.reduce((acc, f) => { acc[f.id] = f; return acc; }, {});

  const handleCardClick = (finding) => {
    if (!onConfigChange) return;
    const statusCycle = ['Green', 'Yellow', 'Red', 'Unknown'];
    const currentStatus = finding.status || 'Unknown';
    const nextStatus = statusCycle[(statusCycle.indexOf(currentStatus) + 1) % statusCycle.length];
    
    let nextScore = 0;
    if (nextStatus === 'Green') nextScore = 100;
    else if (nextStatus === 'Yellow') nextScore = 50;

    onConfigChange({
      ...config,
      dataOverrides: {
        ...dataOverrides,
        [finding.id]: {
            status: nextStatus,
            score: nextScore,
            description: getStatusDescription(finding, nextStatus),
            mode: 'manual'
        }
      }
    });
  };

  const securityFindings = categories.map(cat => {
    const override = dataOverrides[cat.id];
    const sourceId = (override && override.autoSourceId) ? override.autoSourceId : cat.id;
    const sourceFinding = baseFindingsMap[sourceId];

    const baseFinding = {
      id: cat.id,
      title: sourceFinding ? sourceFinding.title : (cat.label || 'New Item'),
      status: sourceFinding ? sourceFinding.status : 'Unknown',
      score: sourceFinding ? sourceFinding.score : 0,
      description: sourceFinding ? (getStatusDescription(sourceFinding, sourceFinding.status)) : 'Configure this item',
      metrics: sourceFinding ? sourceFinding.metrics : [],
      category: 'Security'
    };

    let finalFinding = { ...baseFinding, isManual: false };

    if (override && (override.status || override.mode === 'manual')) {
      const statusToScore = { 'Green': 100, 'Yellow': 50, 'Red': 0, 'Unknown': 0 };
      finalFinding = {
        ...baseFinding,
        status: override.status,
        score: override.score !== undefined ? override.score : (statusToScore[override.status] || 0),
        description: override.description || getStatusDescription(baseFinding, override.status),
        isManual: true
      };
    } else if (override && override.mode === 'dataSource') {
      const result = evaluateDataSourceConfig(data, override);
      finalFinding = {
        ...baseFinding,
        status: result.status,
        score: result.score,
        description: result.description,
        metrics: result.metrics.length > 0 ? result.metrics : baseFinding.metrics,
        isDataSource: true
      };
    }

    if (override?.description && (!override.mode || override.mode === 'auto')) {
      finalFinding.description = override.description;
    }

    if (override?.displayName) {
      finalFinding.title = override.displayName;
    } else if (cat.label) {
      finalFinding.title = cat.label;
    }

    return finalFinding;
  });

  const calculateAuditScore = () => {
    const knownFindings = securityFindings.filter(f => f.status !== 'Unknown');
    if (knownFindings.length === 0) return 0;
    const scores = knownFindings.map(f => f.score);
    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    return Number.isInteger(avgScore) ? avgScore : parseFloat(avgScore.toFixed(2));
  };

  const auditScore = calculateAuditScore();

  return (
    <BaseScanGridWidget
      findings={securityFindings}
      title={title}
      auditScore={auditScore}
      onCardClick={handleCardClick}
      style={style}
      config={config}
      theme={theme}
    />
  );
};
